import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect, useMemo } from 'react';
import { Box, Flex, Button, useToast } from '@chakra-ui/core';
import isEqual from 'lodash/isEqual';
import { DialogFlowAgent, FitBand, ParameterItem, PaneHandle, PaneProps } from '../accountParameter.types';
import { GenericSettingsPane, GenericSettingsPaneHandle } from './GenericSettingsPane';
import { useStoreState } from '../../../../models/hooks';
import { DialogFlowAgentsManager } from './DialogFlowAgentsManager';
import { useTranslation } from 'react-i18next';

interface IntegrationsState {
  rows: ParameterItem[];
  agents: DialogFlowAgent[];
  fitBand: FitBand;
}

export const IntegrationsPane = forwardRef<PaneHandle, PaneProps>(({ onSave, setDirty, showActions }, ref) => {
  const toast = useToast();
  const genericPaneRef = useRef<GenericSettingsPaneHandle>(null);
  const account = useStoreState((state) => state.app.accounts);
  const { t } = useTranslation('accountParameter');

  const initialIntegrationItemsConstant: ParameterItem[] = useMemo(
    () => [
      {
        key: 'modernHireEnable',
        label: t('integrationsPane.competencyEvaluation') as string,
        description: t('integrationsPane.competencyEvaluationDescription') as string,
        value: account?.configuration?.modernHireEnable ?? false,
        type: 'boolean' as const,
      },
      {
        key: 'dialogFlowEnabled',
        label: t('integrationsPane.dialogFlowEnabled') as string,
        description: t('integrationsPane.dialogFlowEnabledDescription') as string,
        value: account?.configuration?.dialogFlowEnabled ?? false,
        type: 'boolean' as const,
      },
      {
        key: 'fitbandTemplateEnabled',
        label: t('integrationsPane.fitbandTemplateEnabled') as string,
        description: t('integrationsPane.fitbandTemplateEnabledDescription') as string,
        value: account?.configuration?.fitbandTemplateEnabled ?? false,
        type: 'boolean' as const,
      },
      {
        key: 'languageEvaluationTemplateEnabled',
        label: t('integrationsPane.languageEvaluationTemplateEnabled') as string,
        description: t('integrationsPane.languageEvaluationTemplateEnabledDescription') as string,
        value: account?.configuration?.languageEvaluationTemplateEnabled ?? false,
        type: 'boolean' as const,
      },
    ],
    [account, t],
  );

  const initialIntegrationState = useMemo(
    () => ({
      rows: JSON.parse(JSON.stringify(initialIntegrationItemsConstant)) as ParameterItem[],
      agents: (account?.dialogFlowAgents ?? []).map((a) => ({
        ...(a as any),
        displayName: (a as any).displayName ?? '',
      })) as DialogFlowAgent[],
      fitBand: (account?.configuration?.fitBand ?? { poorFit: 25, mediumFit: 50, goodFit: 75 }) as FitBand,
    }),
    [account, initialIntegrationItemsConstant],
  );

  const [initialState, setInitialState] = useState<IntegrationsState>(initialIntegrationState);
  const [integrationRows, setIntegrationRows] = useState<ParameterItem[]>(initialState.rows);
  const [dialogflowAgents, setDialogflowAgents] = useState<DialogFlowAgent[]>(initialState.agents);
  const [fitBand, setFitBand] = useState<FitBand>(initialState.fitBand);

  const [genericPaneDirty, setGenericPaneDirty] = useState(false);
  const agentsDirty = !isEqual(dialogflowAgents, initialState.agents);
  const fitBandDirty = !isEqual(fitBand, initialState.fitBand);

  const isDirty = genericPaneDirty || agentsDirty || fitBandDirty;

  useEffect(() => {
    if (!isDirty) {
      setInitialState(initialIntegrationState);
      setIntegrationRows(initialIntegrationState.rows);
      setDialogflowAgents(initialIntegrationState.agents);
      setFitBand(initialIntegrationState.fitBand);
    }
  }, [initialIntegrationState, isDirty]);

  useEffect(() => {
    setDirty(genericPaneDirty || agentsDirty || fitBandDirty);
  }, [genericPaneDirty, agentsDirty, fitBandDirty, setDirty]);

  const handleSaveAll = async () => {
    const updates: Record<string, any> = {};
    integrationRows.forEach((row) => {
      const initialItem = initialState.rows.find((item) => item.key === row.key);
      if (initialItem && !isEqual(row.value, initialItem.value)) {
        updates[row.key] = row.value;
      }
    });

    if (agentsDirty) {
      updates.dialogFlowAgents = dialogflowAgents;
    }
    if (fitBandDirty) {
      updates.fitBand = fitBand;
    }

    if (Object.keys(updates).length > 0) {
      const success = await onSave(updates);
      if (success) {
        setInitialState({
          rows: JSON.parse(JSON.stringify(integrationRows)),
          agents: JSON.parse(JSON.stringify(dialogflowAgents)),
          fitBand: JSON.parse(JSON.stringify(fitBand)),
        });
        genericPaneRef.current?.resetDirtyState();
      }
    } else {
      toast({ status: 'info', title: t('integrationsPane.noChangesToSave'), isClosable: true });
    }
  };

  const handleResetAll = () => {
    setIntegrationRows(initialState.rows);
    setDialogflowAgents(initialState.agents);
    setFitBand(initialState.fitBand);
    genericPaneRef.current?.reset();
  };

  useImperativeHandle(ref, () => ({
    save: handleSaveAll,
    reset: handleResetAll,
  }));

  return (
    <Box>
      {showActions && isDirty && (
        <Flex justify="flex-end" my={4}>
          <Button variant="ghost" mr={3} onClick={handleResetAll}>
            {t('integrationsPane.reset')}
          </Button>
          <Button colorScheme="blue" onClick={handleSaveAll}>
            {t('integrationsPane.save')}
          </Button>
        </Flex>
      )}
      <GenericSettingsPane
        ref={genericPaneRef}
        setDirty={setGenericPaneDirty}
        showInlineActions={false}
        items={initialState.rows}
        onRowsChange={setIntegrationRows}
      />
      <DialogFlowAgentsManager value={dialogflowAgents} onChange={setDialogflowAgents} baselineAgents={initialState.agents} />
    </Box>
  );
});
